import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { CrossingGenerateField } from './template/MasterDocumentTemplate.types';
import { useMasterDocumentTranslation } from './i18n';

export interface CrossingGenerateModalProps {
  open: boolean;
  classLabel: string;
  fields: CrossingGenerateField[];
  onConfirm: (headerValues: Record<string, unknown>) => void;
  onCancel: () => void;
  isLoading?: boolean;
  error?: string;
}

export function CrossingGenerateModal({
  open,
  classLabel,
  fields,
  onConfirm,
  onCancel,
  isLoading = false,
  error,
}: CrossingGenerateModalProps) {
  const { t } = useMasterDocumentTranslation();
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) {
      setValues({});
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onCancel]);

  if (!open) return null;

  const hasFields = fields.length > 0;

  const confirmDisabled =
    isLoading ||
    fields.some((f) => f.required && !String(values[f.key] ?? '').trim());

  function handleConfirm() {
    const headerValues: Record<string, unknown> = {};
    for (const f of fields) {
      headerValues[f.key] = values[f.key] ?? '';
    }
    onConfirm(headerValues);
  }

  return (
    <>
      <div
        className="fixed inset-0 z-[300] bg-black/30 animate-[fadeIn_150ms_ease-out]"
        aria-hidden="true"
      />

      <div
        className={clsx(
          'fixed z-[301] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
          'w-[calc(100%-32px)] max-w-[440px]',
          'bg-bg-primary rounded-2xl px-6 py-5',
          'shadow-lg',
          'animate-[fadeIn_150ms_ease-out]',
          'dark:bg-dark-bg-primary dark:shadow-none dark:border dark:border-dark-border-primary',
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="crossing-generate-modal-title"
      >
        <button
          type="button"
          onClick={onCancel}
          className="absolute top-4 right-4 p-1 rounded-md hover:bg-background-secondary text-content-secondary transition-all duration-150 focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-2 dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary dark:hover:bg-dark-bg-secondary"
          aria-label={t('master_document.chrome.close')}
        >
          <XMarkIcon className="w-4 h-4" aria-hidden="true" />
        </button>

        <h2
          id="crossing-generate-modal-title"
          className="text-base font-bold text-content-primary dark:text-dark-content-primary mb-4 pr-6"
        >
          {t('master_document.crossing.generateTitle', { class: classLabel })}
        </h2>

        {!hasFields && (
          <p className="text-sm text-content-secondary dark:text-dark-content-secondary mb-4">
            {t('master_document.crossing.generateQuestion', { class: classLabel })}
          </p>
        )}

        {hasFields && (
          <div className="flex flex-col gap-3 mb-4">
            {fields.map((field) => (
              <div key={field.key}>
                <label
                  htmlFor={`cgm-field-${field.key}`}
                  className="block text-sm font-semibold text-content-primary dark:text-dark-content-primary mb-1"
                >
                  {field.label}
                  {field.required && (
                    <span className="ml-0.5 text-red-600 dark:text-red-400" aria-hidden="true">
                      {' '}*
                    </span>
                  )}
                </label>
                <input
                  id={`cgm-field-${field.key}`}
                  type={field.type}
                  required={field.required}
                  value={values[field.key] ?? ''}
                  onChange={(e) =>
                    setValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                  }
                  className={clsx(
                    'w-full rounded-lg px-3 py-2 text-sm',
                    'border border-border-primary bg-bg-primary text-content-primary',
                    'placeholder:text-content-tertiary',
                    'transition-colors duration-150',
                    'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-2',
                    'dark:bg-dark-bg-primary dark:border-dark-border-primary dark:text-dark-content-primary',
                    'dark:placeholder:text-dark-content-secondary',
                    'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
                  )}
                />
              </div>
            ))}
          </div>
        )}

        {error && (
          <div
            className={clsx(
              'bg-red-50 border border-red-200 rounded-lg px-3 py-2',
              'text-sm text-red-700 mb-3',
              'dark:bg-red-950/20 dark:border-red-800 dark:text-red-300',
            )}
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-semibold',
              'border border-border-primary text-content-tertiary hover:bg-background-secondary transition-all duration-150',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-2',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
              'dark:border-dark-border-primary dark:text-dark-content-secondary dark:hover:bg-dark-bg-secondary',
              isLoading && 'opacity-50 cursor-not-allowed',
            )}
          >
            {t('master_document.chrome.cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={confirmDisabled}
            className={clsx(
              'flex items-center gap-2 px-5 py-2 rounded-md text-sm font-semibold transition-all duration-150',
              'bg-primary-inverse-background text-white hover:bg-primary-custom-700',
              'dark:bg-dark-bg-inverse dark:text-dark-content-inverse dark:hover:bg-dark-bg-inverse/90',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-2',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
              confirmDisabled && 'opacity-50 cursor-not-allowed',
            )}
          >
            {isLoading && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
                className="w-4 h-4 animate-spin"
              >
                <path
                  fillRule="evenodd"
                  d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            {t('master_document.chrome.confirm')}
          </button>
        </div>
      </div>
    </>
  );
}
